import { useState } from 'react';
import {
  Building2,
  Warehouse,
  Rocket,
  DoorOpen,
  Star,
  Trash2,
  Plus,
  IdCard,
  Flame,
  Landmark,
  Target,
  TriangleAlert,
  Construction,
  Milestone,
  MapPin,
  type LucideIcon,
} from 'lucide-react';
import type { UnitLocation, LocationKind } from '@vtak/shared';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useMapStore } from '@/stores/map-store';
import { useLocationsQuery, useCreateLocation, useDeleteLocation } from '@/queries/use-location-queries';
import { AddLocationForm } from './locations-add-form';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/** Distinct icon per location kind */
const KIND_ICON: Record<LocationKind, LucideIcon> = {
  base: Building2,
  annex: Warehouse,
  'launch-complex': Rocket,
  'access-point': DoorOpen,
  saved: Star,
  gate: DoorOpen,
  'pass-id': IdCard,
  'fire-station': Flame,
  facility: Building2,
  landmark: Landmark,
  mission: Target,
  hazard: TriangleAlert,
  roadblock: Construction,
  waypoint: Milestone,
  custom: MapPin,
};

/** Human label per kind */
const KIND_LABEL: Record<LocationKind, string> = {
  base: 'Base',
  annex: 'Annex',
  'launch-complex': 'Launch Complex',
  'access-point': 'Access Point',
  saved: 'Saved',
  gate: 'Gate',
  'pass-id': 'Pass & ID',
  'fire-station': 'Fire Station',
  facility: 'Facility',
  landmark: 'Landmark',
  mission: 'Mission',
  hazard: 'Hazard',
  roadblock: 'Roadblock',
  waypoint: 'Waypoint',
  custom: 'Custom',
};

/** Unit + personal saved locations with fly-to and quick capture of the current view. */
export function LocationsPanel(): React.JSX.Element {
  const workspace = useWorkspaceStore((s) => s.workspace);
  const role = useWorkspaceStore((s) => s.role);
  const unitId = workspace?.unitId ?? null;
  const query = useLocationsQuery(unitId);

  if (!unitId) {
    return <EmptyState text="No active unit." />;
  }

  const locations = query.data ?? [];
  const unitLocations = locations.filter((loc) => loc.kind !== 'saved' && loc.ownerSub === null);
  const saved = locations.filter((loc) => loc.kind === 'saved' || loc.ownerSub !== null);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex flex-col gap-2 border-b border-echo-border p-3">
        <AddLocationForm unitId={unitId} isAdmin={role === 'admin'} />
        <SaveCurrentView unitId={unitId} />
      </div>
      <ScrollArea className="flex-1">
        <div className="flex flex-col gap-4 p-3">
          <Section title="Unit Locations" rows={unitLocations} role={role} unitId={unitId} />
          <Section title="Saved" rows={saved} role={role} unitId={unitId} emptyHint="Save the current view to add one." />
          {query.isLoading && <p className="text-center text-xs text-muted-foreground">Loading…</p>}
        </div>
      </ScrollArea>
    </div>
  );
}

/** A titled list section of location rows. */
function Section({
  title,
  rows,
  role,
  unitId,
  emptyHint,
}: {
  title: string;
  rows: UnitLocation[];
  role: string | null;
  unitId: string;
  emptyHint?: string;
}): React.JSX.Element {
  return (
    <div>
      <h3 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
      {rows.length === 0 ? (
        <p className="rounded-md bg-white/[0.02] px-3 py-2 text-xs text-muted-foreground">
          {emptyHint ?? 'None.'}
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {rows.map((loc) => (
            <LocationRow key={loc.id} location={loc} canDelete={role === 'admin' || loc.ownerSub !== null} unitId={unitId} />
          ))}
        </ul>
      )}
    </div>
  );
}

/** A single clickable location row that flies the map to the location. */
function LocationRow({
  location,
  canDelete,
  unitId,
}: {
  location: UnitLocation;
  canDelete: boolean;
  unitId: string;
}): React.JSX.Element {
  const setFlyTo = useMapStore((s) => s.setFlyTo);
  const remove = useDeleteLocation(unitId);
  const Icon = KIND_ICON[location.kind];

  return (
    <li className="group flex items-center gap-2 rounded-md bg-white/[0.04] pr-1.5 transition-colors hover:bg-white/[0.08]">
      <button
        type="button"
        onClick={() => setFlyTo({ lat: location.lat, lng: location.lng, zoom: location.zoom })}
        className="flex flex-1 items-center gap-2.5 rounded-md px-3 py-2 text-left"
      >
        <Icon className="h-4 w-4 shrink-0 text-echo-yellow/80" />
        <span className="flex-1 truncate text-sm text-foreground">{location.name}</span>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{KIND_LABEL[location.kind]}</span>
      </button>
      {canDelete && (
        <button
          type="button"
          title="Delete location"
          onClick={() => remove.mutate(location.id)}
          className="rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:text-red-400 group-hover:opacity-100"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </li>
  );
}

/** Inline "save the current map view" capture form. */
function SaveCurrentView({ unitId }: { unitId: string }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const create = useCreateLocation(unitId);

  function save(): void {
    const trimmed = name.trim();
    if (!trimmed) return;
    const view = currentView();
    create.mutate(
      { name: trimmed, kind: 'saved', lat: view.lat, lng: view.lng, zoom: view.zoom },
      { onSuccess: () => { setName(''); setOpen(false); } },
    );
  }

  if (!open) {
    return (
      <Button size="sm" variant="outline" className="w-full gap-1.5" onClick={() => setOpen(true)}>
        <Plus className="h-3.5 w-3.5" /> Save current view
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Input
        autoFocus
        value={name}
        placeholder="Location name"
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setOpen(false); }}
      />
      <div className="flex gap-2">
        <Button size="sm" className="flex-1" disabled={!name.trim() || create.isPending} onClick={save}>
          {create.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
}

/** Resolve the coordinate to save: last map click, else the unit's default view. */
function currentView(): { lat: number; lng: number; zoom: number } {
  const state = useMapStore.getState();
  if (state.lastClick) return { lat: state.lastClick.lat, lng: state.lastClick.lng, zoom: 12 };
  const view = useWorkspaceStore.getState().workspace?.mapView;
  return { lat: view?.lat ?? 0, lng: view?.lng ?? 0, zoom: view?.zoom ?? 10 };
}

/** Centered muted message for empty states. */
function EmptyState({ text }: { text: string }): React.JSX.Element {
  return (
    <div className="flex flex-1 items-center justify-center p-6">
      <p className="text-center text-xs text-muted-foreground">{text}</p>
    </div>
  );
}
